import React from 'react';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CircularProgress from '@material-ui/core/CircularProgress';
import Fade from '@material-ui/core/Fade';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import { THEME } from 'GlobalStyles';
import useUploadStatus from 'hooks/uploadStatus';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import Text from 'UI/components/atoms/Text';
import { CloseIcon, colors, SuccessIcon } from 'UI/res';

import { useStyles } from './styles';

/**
 * @typedef {Object} FileChipProps
 * @property {boolean} loading
 * @property {boolean} [error]
 * @property {*} file
 * @property {string} fileName
 * @property {string} message
 * @property {Function} [onFileDelete]
 */

/**
 * @param {FileChipProps} props
 */
const FileChip = props => {
  const { loading, error, message, file, fileName, onFileDelete, ...rest } = props;
  const classes = useStyles();
  const status = useUploadStatus('finished', loading, error);

  const handleDeleteClick = () => {
    onFileDelete && onFileDelete(file);
  };

  const design = {
    loading: {
      style: classes.loading,
      adornment: (
        <FPIconButton className={classes.button} disabled tooltipProps={{ title: 'Loading' }}>
          <CircularProgress
            classes={{ root: classes.circularProgressClasses }}
            size={24}
            thickness={4}
          />
        </FPIconButton>
      )
    },
    success: {
      style: classes.success,
      adornment: (
        <FPIconButton className={classes.button} disabled tooltipProps={{ title: 'Success' }}>
          <SuccessIcon fill={colors.white} />
        </FPIconButton>
      )
    },
    finished: {
      style: classes.finished,
      adornment: (
        <FPIconButton
          className={classes.button}
          onClick={handleDeleteClick}
          tooltipProps={{ title: 'Delete' }}
        >
          <CloseIcon fill={colors.grey} />
        </FPIconButton>
      )
    },
    error: {
      style: classes.error,
      adornment: (
        <FPIconButton
          className={classes.button}
          onClick={handleDeleteClick}
          tooltipProps={{ title: 'Delete' }}
        >
          <CloseIcon fill={THEME.palette.error.main} />
        </FPIconButton>
      )
    }
  };

  return (
    <Fade in timeout={1000}>
      <Card classes={{ root: clsx(classes.card, design[status].style) }} elevation={0}>
        <CardActionArea classes={{ root: classes.cardAction }} href={file?.url} {...rest}>
          <Text variant="body1" text={fileName} cropped />
        </CardActionArea>
        {design[status].adornment}
        {error && <Typography variant="body1" text={message} color="error" />}
      </Card>
    </Fade>
  );
};

FileChip.defaultProps = {
  error: false,
  onFileDelete: undefined,
  message: ''
};

export default FileChip;
